/**
 * The lit-disc geometry for a moon glyph: the terminator path, its rotation, the
 * maria table and the earthshine strength.
 *
 * Shared because it is arithmetic and string composition, with no rendering
 * surface. Each app keeps its own element tree — mobile draws `react-native-svg`
 * `<Circle>`/`<Path>`, the webapp draws DOM `<circle>`/`<path>` — and those
 * cannot merge without an adapter keyed on `createElement`, which would be worse
 * than the duplication. What *was* duplicated is everything below: two
 * transcriptions of the same 40 lines, which had to be edited in lockstep
 * (FE#170).
 *
 * Colours deliberately do not appear here. Each app passes its own theme tokens
 * to its own elements; nothing but numbers and path strings crosses this seam.
 */

/**
 * Mare positions on a 100x100 disc, hand-placed to read as the near side.
 *
 * Drawn from a table rather than a photograph so the maria can be tinted per
 * theme and the dark limb is a real tone instead of black at 70 % opacity —
 * which, on the light theme, reads as a hole punched in the card.
 *
 * Callers must mask them to the lit region. Painted across the whole disc and
 * then covered by a translucent shadow they show through the dark limb as pale
 * blobs, which is the one thing a moon must never look like.
 */
export const MOON_MARIA: [
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  opacity: number,
][] = [
  [36, 30, 11, 8.5, 0.2],
  [55, 26, 6.5, 5.5, 0.15],
  [43, 51, 15, 12, 0.17],
  [63, 60, 8, 10, 0.13],
  [29, 62, 6.5, 6, 0.14],
  [68, 41, 5, 5, 0.11],
  [50, 71, 9, 5, 0.1],
  [33, 44, 5, 7, 0.12],
];

/** Below this the maria are noise rather than texture. */
export const MARIA_MIN_SIZE = 22;

/** Earthshine is only visible on a thin crescent; it fades out well before half. */
export const EARTHSHINE_LIMIT = 0.45;

export type DiscGeometryParams = {
  /** Illuminated fraction, 0..1. */
  fraction: number;
  /** Waxing or waning, used only when `tilt` is absent. */
  waxing: boolean;
  /** Rendered edge length, px. */
  size: number;
  /** Bright-limb tilt in degrees, if known. */
  tilt?: number;
};

export type DiscGeometry = {
  /** Disc radius, and the centre in both axes (the disc fills its box). */
  radius: number;
  centre: number;
  /** The terminator outline, always drawn lit from +x. */
  shadowPath: string;
  /** SVG `transform` that turns the +x-lit path to the real orientation. */
  transform: string;
  /** Opacity for the earthshine wash, 0 when it would not be visible. */
  earthshine: number;
  /** Whether the disc is large enough for the maria to read as texture. */
  showMaria: boolean;
};

export const discGeometry = ({
  fraction,
  waxing,
  size,
  tilt,
}: DiscGeometryParams): DiscGeometry => {
  const radius = size / 2;
  const centre = radius;

  // cos(phase angle) = 2f - 1, so the terminator's horizontal radius is R|2f-1|.
  const inner = 2 * fraction - 1;
  const terminatorRx = Math.abs(inner) * radius;
  // Always drawn lit from +x; direction comes from the rotation below. Keeping
  // one path and rotating it means the crescent and gibbous cases cannot drift
  // apart, which is what happened when each was written out separately.
  const terminatorSweep = inner < 0 ? 0 : 1;
  const shadowPath =
    `M ${centre} ${centre - radius} A ${radius} ${radius} 0 0 0 ${centre} ${
      centre + radius
    } ` +
    `A ${terminatorRx} ${radius} 0 0 ${terminatorSweep} ${centre} ${
      centre - radius
    } Z`;

  // Rounded: a tilt of -106.6 otherwise yields rotate(16.599999999999994), which
  // is noise in the rendered tree and in every snapshot that touches it.
  const rotation = Number(
    (tilt === undefined ? (waxing ? 0 : 180) : -tilt - 90).toFixed(3),
  );

  return {
    radius,
    centre,
    shadowPath,
    transform: `rotate(${rotation} ${centre} ${centre})`,
    earthshine:
      fraction < EARTHSHINE_LIMIT ? 0.1 * (1 - fraction / EARTHSHINE_LIMIT) : 0,
    showMaria: size >= MARIA_MIN_SIZE,
  };
};
